// client/src/components/pages/LexiStar/Reports/TimeZoneSelect.tsx
//
// ST-00273: the schedule's time zone, picked from a searchable list of IANA zones (type
// "joh" or "+02" to narrow it) instead of typed in. The common zones come first, every
// label carries the zone's current UTC offset. A stored value that is not a zone (typed
// before this list existed) is shown as it is, with a warning asking to choose one.
import { useMemo } from 'react';
import { ThemedSelect } from '../../../../types/commonComponents';
import {
  COMMON_TIME_ZONES,
  allTimeZones,
  isKnownTimeZone,
  timeZoneLabel,
} from '../../../../utils/timeZones';

export interface TimeZoneSelectProps {
  id?: string;
  value: string;
  onChange: (tz: string) => void;
  className?: string;
}

export default function TimeZoneSelect({ id, value, onChange, className }: TimeZoneSelectProps) {
  const { common, rest } = useMemo(() => {
    const now = new Date();
    const all = allTimeZones();
    const commonSet = new Set<string>(COMMON_TIME_ZONES);
    return {
      common: COMMON_TIME_ZONES.filter((z) => all.includes(z) || z === 'UTC').map((z) => ({
        value: z,
        label: timeZoneLabel(z, now),
      })),
      rest: all.filter((z) => !commonSet.has(z)).map((z) => ({ value: z, label: timeZoneLabel(z, now) })),
    };
  }, []);

  const known = isKnownTimeZone(value);
  const listed = common.some((o) => o.value === value) || rest.some((o) => o.value === value);

  return (
    <div className="min-w-0">
      <ThemedSelect
        id={id}
        aria-label="Time zone"
        value={value}
        onChange={(e) => onChange(String(e.target.value))}
        placeholder="Search for a time zone…"
        className={className}
      >
        {value && !listed && (
          // A stored zone outside the list: an IANA link (kept), or not a zone at all.
          <option value={value} disabled={!known}>
            {known ? timeZoneLabel(value) : `${value} (not a known time zone)`}
          </option>
        )}
        <optgroup label="Common">
          {common.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </optgroup>
        <optgroup label="All time zones">
          {rest.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </optgroup>
      </ThemedSelect>
      {value && !known && (
        <p role="alert" className="mt-1 text-xs text-amber-800">
          The saved time zone “{value}” is not a known time zone, so the report is sent in
          Africa/Johannesburg time. Choose a time zone from the list.
        </p>
      )}
    </div>
  );
}
